import {
  ArrowNarrowRightIcon,
  CheckCircleIcon,
  XCircleIcon,
} from '@langchain/untitled-ui-icons';
import { CircularProgress } from '@mui/joy';

import { isEqual } from 'lodash-es';
import { memo, useEffect, useMemo, useRef, useState } from 'react';

import { DatasetSessionCompareTable } from '@/Pages/DatasetSessionCompare/components/DatasetSessionCompareTable';
import {
  DatasetSessionCompareSettings,
  FEEDBACK_COLUMNS_DEFAULT_WIDTH,
  TextDisplayMode,
} from '@/Pages/DatasetSessionCompare/constants';
import { ExpandableErrorAlert } from '@/components/ExpandableErrorAlert';
import { SingleFeedbackChipWithSourceRunAction } from '@/components/FeedbackChips';
import { Tooltip } from '@/design-system';
import { useColorScheme } from '@/hooks/useColorScheme';
import { useStateFromSearchParams } from '@/hooks/useStateFromSearchParams';
import { useOrganizationId } from '@/hooks/useSwr';
import { ExampleSchemaWithRunsAndOptionalFields } from '@/types/schema';
import { cn } from '@/utils/tailwind';
import { useLocalStorageState } from '@/utils/use-local-storage-state';

import { ExperimentColumnHeader } from '../DatasetSessionCompare/components/SingleExperimentColumns/ExperimentColumnHeader';
import { FeedbackColumnHeader } from '../DatasetSessionCompare/components/SingleExperimentColumns/FeedbackColumns/FeedbackColumnHeaders';
import { useFeedbackStyling } from '../DatasetSessionCompare/components/SingleExperimentColumns/FeedbackColumns/hooks/feedback-styling';
import { Loader } from '../DatasetSessionCompare/components/SingleExperimentColumns/Loader';
import { SingleExperimentCell } from '../DatasetSessionCompare/components/SingleExperimentColumns/SingleExperimentCell';
import { isRealRun } from '../DatasetSessionCompare/utils/isRealRun';
import { useTableHeightFromRef } from '../DatasetSessionCompare/utils/useTableHeightFromRef';
import { useAlignEvaluatorInfo } from '../Playground/PlaygroundHome/hooks/useAlignEvaluator';
import {
  useDeep,
  usePlaygroundStore,
} from '../Playground/PlaygroundHome/hooks/usePlaygroundStore';
import { useEvaluatorFeedbackKeysFromRules } from '../Playground/utils/use-evaluator-feedback-keys';
import { EvaluatorAlignmentScoreChip } from './EvaluatorAlignmentScoreChip';
import { transformAlignmentDsExamples } from './utils/transformAlignmentDsExamples';

const HUMAN_SCORE_COLUMN_INDEX = 4;
const LLM_SCORE_COLUMN_INDEX = 5;
const ALIGNMENT_COLUMN_INDEX = 6;
const LLM_COLUMN_MIN_WIDTH = 170;

export const EvaluatorPlaygroundExperimentView = memo(
  ({
    datasetId,
    displaySettingsState,
    exampleRuns,
    shouldHighlightExamples,
  }: {
    datasetId: string | undefined;
    displaySettingsState: DatasetSessionCompareSettings;
    exampleRuns: ExampleSchemaWithRunsAndOptionalFields[];
    shouldHighlightExamples: boolean;
  }) => {
    const promptsInfo = usePlaygroundStore(
      useDeep((state) =>
        state.prompts.map((p) => ({
          sessionId: p.sessionId,
          columnId: p.columnId,
        }))
      )
    );
    const inputSourceType = usePlaygroundStore(
      (state) => state.inputSourceType
    );
    const isCreatingNewDataset = usePlaygroundStore(
      (state) => state.isCreatingNewDataset
    );

    const organizationId = useOrganizationId();
    const { rule, originalSessionId } = useAlignEvaluatorInfo();
    const ruleAsArray = useMemo(() => (rule ? [rule] : []), [rule]);
    const { evaluatorFeedbackKeys } = useEvaluatorFeedbackKeysFromRules({
      organizationId,
      rules: ruleAsArray,
    });

    const removeReferenceOutputFromExamples = usePlaygroundStore(
      (state) => state.removeReferenceOutputFromExamples
    );

    const columns = useMemo(
      () =>
        promptsInfo.map((p, index) =>
          p.sessionId
            ? {
                sessionId: p.sessionId,
                columnName: (
                  <div className="flex items-center gap-4">
                    {exampleRuns?.[0]?.runs && (
                      <div className="flex shrink-0 flex-wrap items-center gap-2">
                        {promptsInfo.length > 1
                          ? `Output ${String.fromCharCode(
                              'A'.charCodeAt(0) + index
                            )}`
                          : 'Output'}
                      </div>
                    )}
                  </div>
                ),
                disableHeaderClick: true,
              }
            : {
                columnId: p.columnId,
                columnName:
                  promptsInfo.length > 1
                    ? `Output ${String.fromCharCode('A'.charCodeAt(0) + index)}`
                    : 'Output',
              }
        ) ?? [],
      [promptsInfo, exampleRuns]
    );

    const displaySettingsStateFinal =
      inputSourceType === 'manual'
        ? {
            ...displaySettingsState,
            isReferenceInputHidden: true,
            isReferenceOutputHidden: true,
          }
        : originalSessionId
        ? {
            ...displaySettingsState,
            isReferenceOutputHidden: true,
          }
        : displaySettingsState;

    const updatedExamples = useMemo(
      () =>
        transformAlignmentDsExamples(
          exampleRuns,
          columns[0].columnId ?? columns[0].sessionId
        ),
      [columns, exampleRuns]
    );

    const [columnWidthStorage, setColumnWidthStorage] = useLocalStorageState(
      `ls:evaluator_playground:columnWidthStorage`,
      [] as number[]
    );

    const tableRef = useRef<HTMLDivElement>(null);
    const tableHeight = useTableHeightFromRef(tableRef);

    const [, setOpenRunId] = useStateFromSearchParams('trace', null);

    // keep track of current state of examples. If any new examples are added, we need to highlight them.
    const [prevExamples, setPrevExamples] = useState<string[]>([]);
    const highlightedExamples = useMemo(() => {
      return shouldHighlightExamples
        ? updatedExamples.filter((e) => !prevExamples.includes(e.id))
        : [];
    }, [shouldHighlightExamples, updatedExamples, prevExamples]);
    useEffect(() => {
      const timeoutId = setTimeout(() => {
        setPrevExamples(updatedExamples.map((e) => e.id));
      }, 1000);

      return () => clearTimeout(timeoutId);
    }, [updatedExamples]);

    return (
      <div className="flex h-full flex-1 flex-col overflow-x-auto">
        <DatasetSessionCompareTable
          tableRef={tableRef}
          expectedFeedbackKeys={[]}
          datasetId={datasetId ?? undefined}
          columns={columns}
          examples={updatedExamples}
          highlightedExamples={highlightedExamples?.map((e) => e.id)}
          onRemoveReferenceOutput={removeReferenceOutputFromExamples}
          areExamplesLoading={false}
          setExamplesSize={() => {
            // do nothing
          }}
          hasMoreToLoad={false}
          isNextPageValidating={false}
          displaySettingsState={displaySettingsStateFinal}
          pageTitle="Playground"
          localStoragePrefix="playground"
          datadogPageName="EVALUATOR_PLAYGROUND"
          filteringRegressionsOnSession={null}
          regressionsFilteringToImprovements={false}
          useWindowScroll={false}
          hoveredSession={null}
          setHoveredSession={() => {
            // do nothing
          }}
          dataType={'chat'}
          creatingNew={!!isCreatingNewDataset}
          customExtraHeaderColumn={
            <>
              <HumanScoreColumnHeader
                columnWidthStorage={columnWidthStorage}
                setColumnWidthStorage={setColumnWidthStorage}
                tableHeight={tableHeight}
              />
              <LLMScoreColumnHeader
                columnWidthStorage={columnWidthStorage}
                setColumnWidthStorage={setColumnWidthStorage}
                tableHeight={tableHeight}
              />
              <AlignmentColumnHeader
                columnWidthStorage={columnWidthStorage}
                setColumnWidthStorage={setColumnWidthStorage}
                tableHeight={tableHeight}
                sessionId={promptsInfo[0].sessionId}
              />
            </>
          }
          customExtraTableCell={(example, backgroundStyles) => {
            const originalExampleRun = exampleRuns.find(
              (er) => er.id === example.id
            );
            return (
              <>
                <HumanScoreCell
                  example={originalExampleRun}
                  feedbackKey={evaluatorFeedbackKeys[0]}
                  columnWidthStorage={columnWidthStorage}
                  backgroundStyles={backgroundStyles}
                />
                <LLMScoreCell
                  example={originalExampleRun}
                  feedbackKey={evaluatorFeedbackKeys[0]}
                  columnWidthStorage={columnWidthStorage}
                  runId={
                    isRealRun(originalExampleRun?.runs?.[0])
                      ? originalExampleRun?.runs?.[0]?.id
                      : undefined
                  }
                  setOpenRunId={setOpenRunId}
                  backgroundStyles={backgroundStyles}
                  textDisplayMode={displaySettingsStateFinal.textDisplayMode}
                />
                <AlignmentCell
                  example={originalExampleRun}
                  columnWidthStorage={columnWidthStorage}
                  feedbackKey={'alignment'}
                  backgroundStyles={backgroundStyles}
                  textDisplayMode={displaySettingsStateFinal.textDisplayMode}
                />
              </>
            );
          }}
        />
      </div>
    );
  },
  (prevProps, nextProps) => {
    return isEqual(prevProps, nextProps);
  }
);

const LLMScoreCell = ({
  example,
  feedbackKey,
  columnWidthStorage,
  runId,
  setOpenRunId,
  backgroundStyles,
  textDisplayMode,
}: {
  example?: ExampleSchemaWithRunsAndOptionalFields;
  feedbackKey: string;
  columnWidthStorage: number[];
  runId?: string;
  setOpenRunId: (runId: string | null) => void;
  backgroundStyles: string;
  textDisplayMode: TextDisplayMode;
}) => {
  const loadingState = usePlaygroundStore(
    useDeep((state) => state.loadingState)
  );
  const run = example?.runs?.[0];
  const error = run?.error;
  const output = run?.outputs?.output ?? run?.outputs;
  const [llmScore, otherOutput] =
    typeof output?.[feedbackKey] === 'boolean'
      ? [Number(output[feedbackKey]), undefined] // true → 1, false → 0
      : [undefined, output];

  // TODO: when fetched from the example runs endpoint, the run outputs seem to have a different shape (no nested `output` key within outputs). Look into why that is.
  const comment =
    example?.runs?.[0]?.outputs?.output?.['comment'] ??
    example?.runs?.[0]?.outputs?.['comment'];

  const { isDarkMode } = useColorScheme();
  const { text: textStyle, background: backgroundStyle } = useFeedbackStyling({
    showHeatmap: true,
    allFeedbackStats: [
      { feedback_stats: { avg: llmScore ?? 0, n: 1, values: {} } },
    ],
    isDarkMode,
    feedbackConfig: {
      feedback_key: feedbackKey,
      feedback_config: {
        type: 'continuous',
        min: 0,
        max: 1,
      },
    },
  });
  const traceButton =
    runId && (llmScore !== undefined || error) ? (
      <button
        type="button"
        className={
          'invisible h-fit rounded-md bg-gray-100 p-1 group-hover:visible dark:border dark:border-secondary dark:bg-background'
        }
        onClick={() => setOpenRunId(runId)}
      >
        <Tooltip title="View Evaluator Run">
          <div className="my-auto flex">
            <ArrowNarrowRightIcon className="h-4 w-4 text-[#667085] dark:text-white" />
          </div>
        </Tooltip>
      </button>
    ) : null;

  return (
    <SingleExperimentCell
      columnIndex={LLM_SCORE_COLUMN_INDEX}
      columnWidthStorage={columnWidthStorage}
      isTruncated={false}
      minColumnWidth={LLM_COLUMN_MIN_WIDTH}
      className={backgroundStyles}
    >
      {loadingState === 'loading' ? (
        <Loader />
      ) : llmScore !== undefined ? (
        <div className="group flex w-full items-center gap-2">
          <SingleFeedbackChipWithSourceRunAction
            feedbackKey={feedbackKey}
            avg={llmScore}
            n={1}
            showErrorCount={true}
            showErrorsOutside={true}
            tooltip={error ?? comment}
            hideFeedbackKey
            hideEllipsis
            errors={error ? 1 : 0}
            startDecorator={<></>}
            chipClassName={cn('w-fit rounded-md border-none', backgroundStyle)}
            valueClassName={textStyle}
          />
          {traceButton}
        </div>
      ) : error ? (
        <div className="group flex w-full items-center gap-2">
          {textDisplayMode === TextDisplayMode.COMPACT ? (
            <div className="truncate text-error">{error}</div>
          ) : (
            <ExpandableErrorAlert error={error} />
          )}
          {traceButton}
        </div>
      ) : otherOutput ? (
        <ExpandableErrorAlert
          error={`Error: LLM output an unexpected value of the wrong shape. Try running again or using a more advanced model. Output:\n\n ${JSON.stringify(
            otherOutput
          )}`}
        />
      ) : (
        <div className="text-sm text-quaternary">Click 'Start' to run</div>
      )}
    </SingleExperimentCell>
  );
};

const HumanScoreCell = ({
  example,
  feedbackKey,
  columnWidthStorage,
  backgroundStyles,
}: {
  example?: ExampleSchemaWithRunsAndOptionalFields;
  feedbackKey?: string;
  columnWidthStorage: number[];
  backgroundStyles: string;
}) => {
  const humanScore =
    feedbackKey != null
      ? example?.outputs?.[feedbackKey] === '0' ||
        example?.outputs?.[feedbackKey] === 0
        ? 0
        : example?.outputs?.[feedbackKey] === '1' ||
          example?.outputs?.[feedbackKey] === 1
        ? 1
        : undefined
      : undefined;

  const comment = example?.outputs?.['few_shot_explanation'];

  const { isDarkMode } = useColorScheme();
  const { text: textStyle, background: backgroundStyle } = useFeedbackStyling({
    showHeatmap: true,
    allFeedbackStats: [
      { feedback_stats: { avg: humanScore ?? 0, n: 1, values: {} } },
    ],
    isDarkMode,
    feedbackConfig: {
      feedback_key: feedbackKey ?? '',
      feedback_config: {
        type: 'continuous',
        min: 0,
        max: 1,
      },
    },
  });

  return example && feedbackKey ? (
    <SingleExperimentCell
      columnIndex={HUMAN_SCORE_COLUMN_INDEX}
      columnWidthStorage={columnWidthStorage}
      isTruncated={false}
      minColumnWidth={FEEDBACK_COLUMNS_DEFAULT_WIDTH}
      className={backgroundStyles}
    >
      <SingleFeedbackChipWithSourceRunAction
        feedbackKey={feedbackKey}
        avg={humanScore}
        n={1}
        tooltip={comment}
        hideFeedbackKey
        hideEllipsis
        startDecorator={<></>}
        chipClassName={cn('w-fit rounded-md border-none', backgroundStyle)}
        valueClassName={textStyle}
      />
    </SingleExperimentCell>
  ) : (
    <div className="flex p-4">
      <CircularProgress size={'sm'} />
    </div>
  );
};

const AlignedCheckmark = () => {
  return (
    <div className="flex" data-testid="aligned-checkmark">
      <div className="rounded-full bg-success-primary p-0.5">
        <CheckCircleIcon className="h-4 w-4 text-success" />
      </div>
    </div>
  );
};

const NotAlignedXIcon = () => {
  return (
    <div className="flex" data-testid="not-aligned-x-icon">
      <div className="rounded-full bg-error-primary p-0.5">
        <XCircleIcon className="h-4 w-4 text-error" />
      </div>
    </div>
  );
};

const AlignmentCell = ({
  example,
  columnWidthStorage,
  feedbackKey,
  backgroundStyles,
  textDisplayMode,
}: {
  example?: ExampleSchemaWithRunsAndOptionalFields;
  columnWidthStorage: number[];
  feedbackKey: string;
  backgroundStyles: string;
  textDisplayMode: TextDisplayMode;
}) => {
  const loadingState = usePlaygroundStore(
    useDeep((state) => state.loadingState)
  );
  const run = example?.runs?.[0];
  const feedback_stats = run?.feedback_stats?.[feedbackKey];
  const error = 'Unable to determine alignment score';

  const component = useMemo(() => {
    if (
      run &&
      feedback_stats?.avg == undefined &&
      (feedback_stats?.errors ?? 0) > 0
    ) {
      return (
        <div className="group flex w-full items-center gap-2">
          {textDisplayMode === TextDisplayMode.COMPACT ? (
            <div className="truncate text-error">{error}</div>
          ) : (
            <ExpandableErrorAlert error={error} />
          )}
          {/* blank div to align spacing with the error of the column to the left */}
          <span className="h-6"></span>
        </div>
      );
    } else if (
      loadingState === 'loading' ||
      (run && feedback_stats?.avg == undefined)
    ) {
      return (
        <div className="flex h-5 w-full flex-col items-stretch">
          <Loader />
        </div>
      );
    }
    if (feedback_stats?.avg === 1) {
      return <AlignedCheckmark />;
    }
    if (feedback_stats?.avg === 0) {
      return <NotAlignedXIcon />;
    }
    return null;
  }, [
    run,
    feedback_stats?.avg,
    feedback_stats?.errors,
    loadingState,
    textDisplayMode,
  ]);
  return (
    <SingleExperimentCell
      columnIndex={ALIGNMENT_COLUMN_INDEX}
      columnWidthStorage={columnWidthStorage}
      isTruncated={false}
      minColumnWidth={FEEDBACK_COLUMNS_DEFAULT_WIDTH}
      className={backgroundStyles}
    >
      {component}
    </SingleExperimentCell>
  );
};

const HumanScoreColumnHeader = ({
  columnWidthStorage,
  setColumnWidthStorage,
  tableHeight,
}: {
  columnWidthStorage: number[];
  setColumnWidthStorage: (fromPrev: (prev: number[]) => number[]) => void;
  tableHeight: number;
}) => {
  return (
    <ExperimentColumnHeader
      columnIndex={HUMAN_SCORE_COLUMN_INDEX}
      columnName={'Human'}
      tableHeight={tableHeight}
      columnWidthStorage={columnWidthStorage}
      setColumnWidthStorage={setColumnWidthStorage}
      className="h-[45px]"
      minColumnWidth={FEEDBACK_COLUMNS_DEFAULT_WIDTH}
    >
      <div className="h-full">
        <div className="flex flex-row items-center gap-2">Human</div>
      </div>
    </ExperimentColumnHeader>
  );
};

const LLMScoreColumnHeader = ({
  columnWidthStorage,
  setColumnWidthStorage,
  tableHeight,
}: {
  columnWidthStorage: number[];
  setColumnWidthStorage: (fromPrev: (prev: number[]) => number[]) => void;
  tableHeight: number;
}) => {
  return (
    <ExperimentColumnHeader
      columnIndex={LLM_SCORE_COLUMN_INDEX}
      columnName={'LLM'}
      tableHeight={tableHeight}
      columnWidthStorage={columnWidthStorage}
      setColumnWidthStorage={setColumnWidthStorage}
      className="h-[45px]"
      minColumnWidth={LLM_COLUMN_MIN_WIDTH}
    >
      <div className="h-full">
        <div className="flex flex-row items-center gap-2">LLM</div>
      </div>
    </ExperimentColumnHeader>
  );
};

const AlignmentColumnHeader = ({
  columnWidthStorage,
  setColumnWidthStorage,
  tableHeight,
  sessionId,
}: {
  columnWidthStorage: number[];
  setColumnWidthStorage: (fromPrev: (prev: number[]) => number[]) => void;
  tableHeight: number;
  sessionId?: string;
}) => {
  return (
    <FeedbackColumnHeader
      feedbackKey={'alignment'}
      evaluatorFeedbackKeys={['alignment']}
      columnIndex={ALIGNMENT_COLUMN_INDEX}
      tableHeight={tableHeight}
      columnWidthStorage={columnWidthStorage}
      setColumnWidthStorage={setColumnWidthStorage}
      className="h-[45px]"
      sessionId={sessionId}
      onChange={undefined}
      runFacets={undefined}
      currentFilters={[]}
      canSort={!!sessionId}
      statDisplay={(stat) => (
        <EvaluatorAlignmentScoreChip alignmentScore={stat} />
      )}
    />
  );
};
